import {
  Area,
  Domain,
  FormMainType,
  IcdArea,
  IcdGroup,
  OrphanetSynonym,
  Sector,
} from '@lib/types';
import { BaseModel } from 'src/app/@shared/base.model';
import { OrganizationSubType, Topic } from '@common/entities';
import { getColorByFormType } from 'src/app/@shared/form/form-helper';
import { getOrganizationSubTypeOptionTranslationKey } from 'src/app/@shared/organization-sub-type-translation';
import { isPublicWebAppBuild } from 'src/app/@shared/is-public-web-app';

export type TopicSource = Topic;
export type OrganizationCountSubType = 'organizationCountGa' | 'organizationCountCommunity'
  | 'organizationCountShGroup' | 'organizationCountBa' | 'organizationCountOther' | 'all';

const ORGANIZATION_COUNT_SUB_TYPE_MAP: { [key in OrganizationCountSubType]?: OrganizationSubType } = {
  organizationCountBa: 'selfHelpAddressBa',
  organizationCountCommunity: 'selfHelpAddressCommunity',
  organizationCountGa: 'selfHelpAddressGa',
  organizationCountOther: 'selfHelpAddressOther',
  organizationCountShGroup: 'selfHelpAddressShGroup',
};

// Markers used by the imported topic taxonomy. Do not match arbitrary abbreviations
// such as (HHT), (HSP), or (AED), which are part of the displayed name.
const TRAILING_TOPIC_METADATA_PATTERN = /\s*(?:\((?:B|FE|FK|G|S|SE|T|TF|V)\)\s*)+$/u;

export const removeTrailingTopicMetadata = (value: string): string => {
  return value.replace(TRAILING_TOPIC_METADATA_PATTERN, '');
};

export class TopicModel extends BaseModel<TopicSource> {
  public legacyId?: string;
  public isRareDisease: boolean;
  public sector: Sector[];
  public area: Area[];
  public domain: Domain[];

  public englishTitle?: string;
  public synonyms?: string[];
  public icd10Codes?: string[];
  public icd11Codes?: string[];
  public icdArea?: IcdArea[];
  public icdGroup?: IcdGroup[];
  public links?: string[];
  public notes?: string;
  public orphanetTopicId?: string;
  public orphanetTopicName?: string;
  public orphanetTopicIcd10Code?: string;
  public orphanetSynonyms?: OrphanetSynonym[];
  public organizationCountTotal?: number;
  public organizationCountSubTypeTotal?: number;
  public organizationCounts: {
    id: OrganizationCountSubType;
    label: string;
    count: number;
    indicatorColor: string;
    organizationSubType?: OrganizationSubType;
  }[] = [];

  public displayName: string;
  public type: FormMainType;

  public verboseTitle: string;

  // eslint-disable-next-line max-statements,max-lines-per-function
  constructor(source: TopicSource) {
    super(source);
    const isPublicWebApp = isPublicWebAppBuild();

    if (isPublicWebApp) {
      this.title = removeTrailingTopicMetadata(this.title);
    }

    this.legacyId = source.legacyId;
    this.isRareDisease = source.isRareDisease;
    this.sector = source.sector;
    this.area = source.area;
    this.domain = source.domain;
    this.englishTitle = source.englishTitle;
    this.synonyms = isPublicWebApp
      ? source.synonyms?.map(removeTrailingTopicMetadata)
      : source.synonyms;
    this.icd10Codes = source.icd10Codes;
    this.icd11Codes = source.icd11Codes;
    this.icdArea = source.icdArea;
    this.icdGroup = source.icdGroup;
    this.links = source.links;
    this.notes = source.notes;
    this.orphanetTopicId = source.orphanetTopicId;
    this.orphanetTopicName = isPublicWebApp && source.orphanetTopicName
      ? removeTrailingTopicMetadata(source.orphanetTopicName)
      : source.orphanetTopicName;
    this.orphanetTopicIcd10Code = source.orphanetTopicIcd10Code;

    if (source.orphanetSynonyms?.length) {
      try {
        this.orphanetSynonyms = source.orphanetSynonyms.map((o) => {
          const synonym = typeof o === 'string'
            ? JSON.parse(o)
            : o;

          if (isPublicWebApp && synonym?.orphanetSynonym) {
            return {
              ...synonym,
              orphanetSynonym: removeTrailingTopicMetadata(synonym.orphanetSynonym),
            };
          }

          return synonym;
        });
      } catch (error) {
        console.error(error?.message);
      }
    }

    this.setOrganizationCounts(source);

    this.displayName = this.title;
    this.type = 'topic'; // set this to apply similar logic as in the organization/person

    this.verboseTitle = this.title + (this.synonyms?.length
      ? `, ${this.synonyms.join(', ')}`
      : '');
  }

  // eslint-disable-next-line max-lines-per-function
  private setOrganizationCounts(source: TopicSource): void {
    this.organizationCounts = [
      {
        count: source.organizationCountGa,
        id: 'organizationCountGa',
        indicatorColor: getColorByFormType('selfHelpAddress', ORGANIZATION_COUNT_SUB_TYPE_MAP.organizationCountGa),
        label: getOrganizationSubTypeOptionTranslationKey('selfHelpAddressGa'),
        organizationSubType: ORGANIZATION_COUNT_SUB_TYPE_MAP.organizationCountGa,
      },
      {
        count: source.organizationCountCommunity,
        id: 'organizationCountCommunity',
        indicatorColor: getColorByFormType('selfHelpAddress', ORGANIZATION_COUNT_SUB_TYPE_MAP.organizationCountCommunity),
        label: getOrganizationSubTypeOptionTranslationKey('selfHelpAddressCommunity'),
        organizationSubType: ORGANIZATION_COUNT_SUB_TYPE_MAP.organizationCountCommunity,
      },
      {
        count: source.organizationCountShGroup,
        id: 'organizationCountShGroup',
        indicatorColor: getColorByFormType('selfHelpAddress', ORGANIZATION_COUNT_SUB_TYPE_MAP.organizationCountShGroup),
        label: getOrganizationSubTypeOptionTranslationKey('selfHelpAddressShGroup'),
        organizationSubType: ORGANIZATION_COUNT_SUB_TYPE_MAP.organizationCountShGroup,
      },
      {
        count: source.organizationCountBa,
        id: 'organizationCountBa',
        indicatorColor: getColorByFormType('selfHelpAddress', ORGANIZATION_COUNT_SUB_TYPE_MAP.organizationCountBa),
        label: getOrganizationSubTypeOptionTranslationKey('selfHelpAddressBa'),
        organizationSubType: ORGANIZATION_COUNT_SUB_TYPE_MAP.organizationCountBa,
      },
      {
        count: source.organizationCountOther,
        id: 'organizationCountOther',
        indicatorColor: getColorByFormType('selfHelpAddress', ORGANIZATION_COUNT_SUB_TYPE_MAP.organizationCountOther),
        label: getOrganizationSubTypeOptionTranslationKey('selfHelpAddressOther'),
        organizationSubType: ORGANIZATION_COUNT_SUB_TYPE_MAP.organizationCountOther,
      },
    ];

    this.organizationCountTotal = this.organizationCounts.reduce((acc, countObj) => {
      return acc + countObj.count;
    }, 0);

    this.organizationCountSubTypeTotal = this.organizationCounts.filter((countObj) => {
      return countObj.count > 0;
    }).length;

    // Add item for all linked organizations
    this.organizationCounts.push({
      count: this.organizationCountTotal,
      id: 'all',
      indicatorColor: 'var(--ion-color-light)',
      label: 'TERM.ALL',
    });
  }
}
